/** Rollen-Sichtbarkeit der Verwaltungs-Tabs (Rechte-Konzept Stufe 1).

    Ein Benutzer kann mehrere Rollen haben (BenutzerRolle); ein Tab ist sichtbar, wenn
    EINE seiner Rollen in der Rollenliste des Tabs steht. Bis /api/me/rechte geladen ist
    (oder wenn die Antwort keine Rollen liefert), gilt nur die Hauptrolle. */

/** Alle Rollen des Benutzers: geladene Rollen aus useRechte, sonst die Hauptrolle. */
export function benutzerRollen(
  geladeneRollen: string[],
  geladen: boolean,
  hauptrolle?: string,
): string[] {
  if (geladen && geladeneRollen.length > 0) return geladeneRollen;
  return hauptrolle ? [hauptrolle] : [];
}

/** Darf ein Benutzer mit diesen Rollen einen Tab mit `tabRollen` sehen?
    Leere Tab-Liste = alle; ohne bekannte Rolle bleibt es beim bisherigen Verhalten
    (alles zeigen); Admin sieht jeden rollen-gesteuerten Tab. */
export function tabRolleErlaubt(tabRollen: string[], rollen: string[]): boolean {
  if (tabRollen.length === 0 || rollen.length === 0) return true;
  if (rollen.includes('Admin')) return true;
  return tabRollen.some(r => rollen.includes(r));
}

/** Standard-Tab der Verwaltung: der erste sichtbare Tab NACH 'warnungen' - 'auftraege',
    wenn sichtbar, sonst der erste sichtbare Tab ausser 'warnungen', sonst der erste
    sichtbare Tab ueberhaupt (die Uebersicht liefert seit den Rollen-Dashboards die
    Ansicht "Dashboards", nicht mehr die Verwaltung). */
export function standardTabId<T extends string>(sichtbareTabIds: T[]): T | undefined {
  const auftraege = sichtbareTabIds.find(id => id === 'auftraege');
  if (auftraege) return auftraege;
  return sichtbareTabIds.find(id => id !== 'warnungen') ?? sichtbareTabIds[0];
}

export type TabType = 'warnungen' | 'kunden' | 'artikel' | 'lager' | 'bewegungen' | 'bestellungen' | 'fertigung' | 'qualitaet' | 'auftraege' | 'aufgaben' | 'kanban' | 'dokumente' | 'vertraege' | 'zahlungen' | 'retouren' | 'sendungen' | 'muster' | 'import' | 'stuecklisten' | 'kataloge' | 'prozesse' | 'projekte' | 'zeitbuchung' | 'materialentnahme' | 'einsaetze' | 'regieberichte';

export interface VerwaltungsTab {
  id: TabType;
  label: string;
  rollen: string[]; // Welche Rollen diesen Tab sehen dürfen (leer = alle)
  modul?: string;   // Falls gesetzt: Tab nur sichtbar wenn dieses Modul aktiv ist
}

/** Tabs der Verwaltung (ManagementPage) mit Rollen- und Modul-Sichtbarkeit. Liegt hier,
    damit die Dashboards mit derselben Regel prüfen, ob ein Kachel-Ziel für die Rollen
    des Benutzers überhaupt erreichbar ist (sichtbareTabIds). */
export const ALL_TABS: VerwaltungsTab[] = [
  // Übersicht (immer sichtbar; die Kennzahlen liegen in der Ansicht "Dashboards")
  { id: 'warnungen',    label: 'Warnungen',     rollen: [] },
  // Stammdaten
  { id: 'kunden',       label: 'Kontakte',       rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Lagerleiter', 'Verkäufer', 'Buchhaltung'], modul: 'partner' },
  { id: 'artikel',      label: 'Artikel',        rollen: [], modul: 'artikel' },
  // Lager & Einkauf
  { id: 'lager',        label: 'Lager',          rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Produktionsleiter'], modul: 'lager' },
  { id: 'bewegungen',   label: 'Bewegungen',     rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter'], modul: 'lager' },
  { id: 'bestellungen', label: 'Bestellungen',   rollen: ['Admin', 'Geschäftsführer', 'Einkäufer'], modul: 'bestellung' },
  // Produktion & Qualität
  { id: 'fertigung',    label: 'Fertigung',      rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter'], modul: 'fertigungsauftrag' },
  { id: 'qualitaet',    label: 'Qualität',       rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter', 'Lagerleiter'], modul: 'qualitaet' },
  // Verkauf & Finanzen
  // Arbeiter (Monteur): sieht hier nur die ihm zugewiesenen Aufträge/Baustellen - der
  // Datenbereich kommt aus der Prüfstelle (Backend), die Tab-Liste regelt nur die Navigation.
  { id: 'auftraege',    label: 'Aufträge',       rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Einkäufer', 'Lagerleiter', 'Arbeiter'], modul: 'auftrag' },
  { id: 'aufgaben',     label: 'Aufgaben',        rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter'] },
  { id: 'kanban',       label: 'Kanban',          rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter'], modul: 'auftrag' },
  { id: 'dokumente',    label: 'Dokumente',      rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Buchhaltung'] },
  { id: 'vertraege',   label: 'Verträge',       rollen: ['Admin', 'Geschäftsführer', 'Einkäufer', 'Verkäufer', 'Buchhaltung'] },
  { id: 'zahlungen',   label: 'Zahlungen',      rollen: ['Admin', 'Geschäftsführer', 'Buchhaltung'], modul: 'zahlung' },
  { id: 'retouren',    label: 'Retouren',       rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Lagerleiter', 'Buchhaltung'], modul: 'retoure' },
  { id: 'sendungen',   label: 'Sendungen',      rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Verkäufer'], modul: 'sendung' },
  // Admin (Querschnitt — immer sichtbar für berechtigte Rollen)
  { id: 'muster',       label: 'Systemvorschläge', rollen: ['Admin'] },
  { id: 'import',       label: 'Import',             rollen: ['Admin', 'Einkäufer'] },
  { id: 'stuecklisten', label: 'Stücklisten',        rollen: ['Admin', 'Einkäufer'] },
  { id: 'kataloge',     label: 'Kataloge',           rollen: ['Admin', 'Einkäufer'] },
  { id: 'prozesse',     label: 'Prozesse',            rollen: ['Admin'] },
  // Handwerk / Projekt (module-gated)
  { id: 'projekte',     label: 'Projekte',           rollen: ['Admin', 'Geschäftsführer', 'Verkäufer', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter', 'Arbeiter'], modul: 'projekt' },
  { id: 'zeitbuchung',  label: 'Zeitbuchung',        rollen: ['Admin', 'Geschäftsführer', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter', 'Arbeiter'], modul: 'zeitbuchung' },
  { id: 'materialentnahme', label: 'Materialentnahme', rollen: ['Admin', 'Geschäftsführer', 'Lagerleiter', 'Produktionsleiter', 'Projektleiter', 'Projektmitarbeiter', 'Arbeiter'], modul: 'materialentnahme' },
  { id: 'einsaetze', label: 'Einsatzplanung', rollen: ['Admin', 'Geschäftsführer', 'Projektleiter', 'Projektmitarbeiter', 'Arbeiter'], modul: 'einsatz' },
  { id: 'regieberichte', label: 'Regieberichte', rollen: ['Admin', 'Geschäftsführer', 'Projektleiter', 'Projektmitarbeiter', 'Buchhaltung', 'Arbeiter'], modul: 'einsatz' },
];

/** Ids der Verwaltungs-Tabs, die diese Rollen sehen (Rollenregel; die Modul-Sichtbarkeit
    prüft ManagementPage zusätzlich, Board-Kacheln sind im Backend bereits modulgefiltert).
    Reihenfolge wie ALL_TABS. */
export function sichtbareTabIds(rollen: string[]): TabType[] {
  return ALL_TABS.filter(tab => tabRolleErlaubt(tab.rollen, rollen)).map(tab => tab.id);
}

/** Navigationsziel (Dashboards-Kachel, Wochenplan …), Form wie NavigiereDetail. */
export interface NavigationsZiel {
  ansicht: 'management' | 'chat' | 'einstellungen';
  tab: string | null;
  params?: Record<string, unknown>;
}

/** Kann der Benutzer mit diesen sichtbaren Tabs das Ziel öffnen? Dieselbe Regel wie
    ManagementPage (wendeNavigationAn): `auftrag_id` braucht den Aufträge-Tab, `partner_id` den Kontakte-Tab,
    sonst der genannte Tab; ohne Tab landet man auf dem Standard-Tab. Chat und Einstellungs-Drawer
    sind immer erreichbar (der Drawer fällt bei unsichtbarer Sektion auf seinen Default
    zurück).
    `erreichbareTabs === null`: Rollen noch nicht geladen, nichts sperren. */
export function zielErreichbar(ziel: NavigationsZiel, erreichbareTabs: string[] | null): boolean {
  if (ziel.ansicht !== 'management' || erreichbareTabs === null) return true;
  if (typeof ziel.params?.auftrag_id === 'number') return erreichbareTabs.includes('auftraege');
  if (typeof ziel.params?.partner_id === 'number') return erreichbareTabs.includes('kunden');
  return ziel.tab === null || erreichbareTabs.includes(ziel.tab);
}
